/**
 * lightFieldConfig — config + pure placement for a LightField: a managed
 * cloud of SoftLights that fills a region with slow-drifting blurred light.
 *
 * This is the "repeat the look anywhere" primitive — a scene declares
 * "N soft lights of palette X across this region" and gets the whole
 * djmsqrvve light-bed for ~free. Placement is seeded + deterministic (stable
 * across reloads, no flicker) and each light gets its own resolved SoftLight
 * spec (independent drift bearing + breathing phase) so the cloud never reads
 * as a synchronised grid.
 *
 * Pure math only (no Pixi) — the LightField render class (lightField.ts)
 * turns these placements into SoftLight instances.
 *
 * @see ./SPEC.md — language-neutral lift spec (config + algorithm + renderer seam).
 */
import { seededRand } from '../atmosphere/atmospherePlacement';
import type { Region } from '../atmosphere/atmospherePlacement';
import { resolveSoftLight, type SoftLightSpec } from './softLightConfig';
import type { VfxBlendMode } from '../../rendering/IRenderer';
import { lerp } from '../../../shared/utils/MathUtils';

export interface LightFieldConfig {
  /** How many soft lights to scatter. Sparse reads best (8–20). */
  count: number;
  /** Colors the lights are tinted from (round-robin by seed). */
  palette: number[];
  /** Per-light size band (px). Defaults to the SoftLight default size. */
  sizeMin?: number;
  sizeMax?: number;
  /** Per-light opacity band (passed through to each SoftLight). */
  alphaMin?: number;
  alphaMax?: number;
  /** Per-light drift radius (px). */
  driftRadius?: number;
  /** Per-light cycle band (ms). */
  periodMinMs?: number;
  periodMaxMs?: number;
  blendMode?: VfxBlendMode;
}

/** One resolved light in a field — where it anchors + its full spec + phase. */
export interface PlacedLight {
  x: number;
  y: number;
  /** Breathing phase offset in [0,1) so lights pulse out of sync. */
  phase: number;
  spec: SoftLightSpec;
}

/** A reasonable default cloud for a quick scene. */
export const DEFAULT_LIGHT_FIELD: LightFieldConfig = {
  count: 12,
  palette: [0x9fd8ff, 0x01ffc3, 0xff71ce],
};

/**
 * Scatter `count` soft lights across `region`, seeded by `baseSeed`. Each
 * light's position, color, size, drift bearing, cycle length and breathing
 * phase are derived deterministically from the seed — so the field is stable
 * yet varied. Returns [] for count <= 0.
 */
export function placeLightField(cfg: LightFieldConfig, region: Region, baseSeed: number): PlacedLight[] {
  const out: PlacedLight[] = [];
  if (cfg.count <= 0) return out;

  const sizeMin = cfg.sizeMin;
  const sizeMax = cfg.sizeMax;

  for (let i = 0; i < cfg.count; i += 1) {
    const s = baseSeed + i * 9.73;
    const color = cfg.palette[Math.floor(seededRand(s + 3) * cfg.palette.length) % cfg.palette.length];
    const size =
      sizeMin !== undefined && sizeMax !== undefined
        ? lerp(sizeMin, sizeMax, seededRand(s + 2))
        : undefined;

    // The per-light seed feeds resolveSoftLight too, so drift + period vary.
    const spec = resolveSoftLight(
      {
        color,
        size,
        alphaMin: cfg.alphaMin,
        alphaMax: cfg.alphaMax,
        driftRadius: cfg.driftRadius,
        periodMinMs: cfg.periodMinMs,
        periodMaxMs: cfg.periodMaxMs,
        blendMode: cfg.blendMode,
      },
      Math.floor(s * 100) + 1,
    );

    out.push({
      x: lerp(region.x0, region.x1, seededRand(s)),
      y: lerp(region.y0, region.y1, seededRand(s + 1)),
      phase: seededRand(s + 6),
      spec,
    });
  }
  return out;
}
